import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import api from "../../services/api";
import DatePicker from "../../components/common/DatePicker";
import Select from "../../components/common/Select";

const fundSchema = Yup.object({
  admin_bank_account_id: Yup.string().required("Please select a virtual account"),
  user_bank_account_id: Yup.string().required("Please select a bank account"),
  amount: Yup.number().typeError("Amount must be a number").positive("Amount must be greater than 0").required("Amount is required"),
  payment_mode: Yup.string().required("Please select a payment mode"),
  transaction_id: Yup.string().required("Transaction ID / UTR is required"),
  payment_date: Yup.string().required("Payment date is required"),
  receipt: Yup.mixed().required("Please upload a receipt"),
});

const stats = [
  {
    label: "Total Requests",
    value: "11",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    iconBg: "bg-primary/5",
    iconColor: "text-primary",
  },
  {
    label: "Pending",
    value: "0",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    iconBg: "bg-[#BA1A1A0D]",
    iconColor: "text-[#BA1A1A]",
  },
  {
    label: "Active Virtual Accounts",
    value: "1",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" strokeLinecap="round" />
      </svg>
    ),
    iconBg: "bg-primary-light/5",
    iconColor: "text-primary-light",
  },
  {
    label: "Approved Source Banks",
    value: "3",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 10v11M12 10v11M16 10v11" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    iconBg: "bg-[#FFB78533]",
    iconColor: "text-[#E98434]",
  },
];

const statusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case 'approved': return 'text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600';
    case 'pending':  return 'text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-600';
    case 'rejected': return 'text-[11px] font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-600';
    default:         return 'text-[11px] font-bold px-2.5 py-1 rounded-full bg-gray-50 text-gray-600';
  }
};

export default function FundRequest() {
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [userBankAccounts, setUserBankAccounts] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    total_requests: 0,
    pending_requests: 0,
    approved_requests: 0,
    rejected_requests: 0,
    active_virtual_accounts: 0,
    approved_source_banks: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [tableLoading, setTableLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [form, setForm] = useState({
    admin_bank_account_id: "",
    user_bank_account_id: "",
    amount: "",
    payment_mode: "",
    transaction_id: "",
    payment_date: "",
    remark: "",
    receipt: null,
  });

  const fetchRequests = (page = 1, filters = {}) => {
    setTableLoading(true);
    const params = new URLSearchParams({ page, limit: 10 });
    if (filters.search) params.append("search", filters.search);
    if (filters.from_date) params.append("from_date", filters.from_date);
    if (filters.to_date) params.append("to_date", filters.to_date);
    if (filters.status) params.append("status", filters.status);
    api.get(`/fund-requests?${params.toString()}`)
      .then((res) => {
        setRequests(res.data?.data?.requests || []);
        setPagination(res.data?.data?.pagination || { current_page: 1, last_page: 1, total: 0 });
      })
      .catch(() => {})
      .finally(() => setTableLoading(false));
  };

  const fetchView = (id) => {
    setViewLoading(true);
    api.get(`/fund-requests/${id}`)
      .then((res) => setViewData(res.data?.data || null))
      .catch(() => {})
      .finally(() => setViewLoading(false));
  };

  useEffect(() => {
    api.get("/fund-requests/form-data")
      .then((res) => {
        const d = res.data?.data || {};
        setVirtualAccounts(d.virtual_accounts || []);
        setUserBankAccounts(d.user_bank_accounts || []);
        setPaymentModes(d.payment_modes || []);
      })
      .catch(() => toast.error("Failed to load form data."));

    api.get("/fund-requests/dashboard")
      .then((res) => {
        setDashboardData(res.data?.data || {});
      })
      .catch(() => {});

    fetchRequests(1);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setForm((prev) => ({ ...prev, receipt: e.target.files?.[0] || null }));
  };

  const handleSubmit = async () => {
    try {
      await fundSchema.validate(form, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const errs = {};
      err.inner.forEach((e) => { errs[e.path] = e.message; });
      setErrors(errs);
      toast.error(err.inner?.[0]?.message || "Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("admin_bank_account_id", form.admin_bank_account_id);
      fd.append("user_bank_account_id", form.user_bank_account_id);
      fd.append("amount", form.amount);
      fd.append("payment_mode", form.payment_mode);
      fd.append("transaction_id", form.transaction_id);
      fd.append("payment_date", form.payment_date);
      fd.append("remark", form.remark);
      if (form.receipt) fd.append("receipt", form.receipt);
      await api.post("/fund-requests", fd);
      toast.success("Fund request submitted successfully!");
      setForm({ admin_bank_account_id: "", user_bank_account_id: "", amount: "", payment_mode: "", transaction_id: "", payment_date: "", remark: "", receipt: null });
      fetchRequests(1);
      api.get("/fund-requests/dashboard").then((res) => setDashboardData(res.data?.data || {})).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit fund request.");
    } finally {
      setSubmitting(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowFormatMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const [exportSearch, setExportSearch] = useState('');
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exportStatus, setExportStatus] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRequests(1, {
        search: exportSearch.trim(),
        from_date: exportFrom,
        to_date: exportTo,
        status: exportStatus,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [exportSearch, exportFrom, exportTo, exportStatus]);

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportSearch) params.append('search', exportSearch);
      if (exportFrom)   params.append('from_date', exportFrom);
      if (exportTo)     params.append('to_date', exportTo);
      if (exportStatus) params.append('status', exportStatus);
      params.append('format', format);
      const res = await api.get(`/fund-requests/export?${params.toString()}`, { responseType: 'blob' });

      const contentType = res.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        toast.info(json.message || 'Export queued. Check Reports section to download.');
        return;
      }

      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      link.setAttribute('download', `fund_requests_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed successfully!');
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const inputCls = "w-full bg-[#F3F4F5] rounded-[12px] px-4 py-3 text-[14px] text-[#191C1D] outline-none focus:ring-1 focus:ring-[#4B2364]/20 focus:border-[#4B2364]/40 placeholder:text-[#6B7280] transition-all";
  const selectCls = inputCls + " appearance-none cursor-pointer";
  const labelCls = "block text-[14px] font-semibold text-[#4C444E] mb-2";

  return (
    <>
        <div className="flex-1 overflow-y-auto px-8 py-7">

          {/* Header */}
          <div className="mb-7">
            <h1 className="text-[36px] font-bold text-primary leading-tight">Fund Request</h1>
          </div>

          {/* Stats Row */}
          <div className="grid lg:grid-cols-4 gap-5 mb-8 md:grid-cols-2 sm:grid-cols-1">
            {stats.map((s) => (
              <div key={s.label} className="bg-white rounded-[16px] px-5 py-5 shadow-[0px_8px_24px_0px_#4B23640A] flex items-end justify-between">
                <div>
                  <p className="text-[14px] font-semibold text-[#4C444E] mb-1.5">{s.label}</p>
                  <p className="text-[30px] font-bold text-primary leading-none">
                    {s.label === "Total Requests" && dashboardData.total_requests}
                    {s.label === "Pending" && dashboardData.pending_requests}
                    {s.label === "Active Virtual Accounts" && dashboardData.active_virtual_accounts}
                    {s.label === "Approved Source Banks" && dashboardData.approved_source_banks}
                  </p>
                </div>
                <div className={`w-8 h-8 rounded-[8px] flex items-center justify-center ${s.iconBg} ${s.iconColor}`}>
                  {s.icon}
                </div>
              </div>
            ))}
          </div>

          {/* Main Form Card */}
          <div className="bg-white rounded-[20px] shadow-[0px_8px_32px_rgba(75,35,100,0.07)] border border-[#F0EBF4]">

            {/* Gradient Header */}
            <div className="px-7 py-6 bg-primary-gradient">
              <p className="text-[24px] font-semibold text-white leading-tight">Create New Fund Request</p>
              {/* <p className="text-[14px] text-[#FFFFFFB2] mt-1">Initiate a manual balance reconciliation request for incoming transfers.</p> */}
            </div>

            {/* Form Body */}
            <div className="px-7 py-7">
              <div className="grid grid-cols-2 gap-x-6 gap-y-5">

                {/* Deposited to Virtual Account */}
                <div>
                  <label className={labelCls}>Recipient Virtual Account</label>
                  <div className="relative">
                    <Select
                      name="admin_bank_account_id"
                      value={form.admin_bank_account_id}
                      onChange={handleChange}
                      placeholder="Select a virtual account"
                      className={selectCls}
                      options={virtualAccounts.map(v => ({ label: `${v.bank_name} (${v.account_number})`, value: String(v.id ?? v.bank_account_id ?? v.admin_bank_account_id ?? "") }))}
                    />
                  </div>
                  {errors.admin_bank_account_id && <p className="text-red-500 text-xs mt-1">{errors.admin_bank_account_id}</p>}
                </div>

                {/* Deposited from Bank Account */}
                <div>
                  <label className={labelCls}>Your Source Bank Account</label>
                  <div className="relative">
                    <Select
                      name="user_bank_account_id"
                      value={form.user_bank_account_id}
                      onChange={handleChange}
                      placeholder="Select your bank account"
                      className={selectCls}
                      options={userBankAccounts.map(u => ({ label: `${u.bank_name} (${u.account_number})`, value: String(u.id ?? u.bank_account_id ?? u.user_bank_account_id ?? "") }))}
                    />
                  </div>
                  {errors.user_bank_account_id && <p className="text-red-500 text-xs mt-1">{errors.user_bank_account_id}</p>}
                </div>

                {/* Amount */}
                <div>
                  <label className={labelCls}>Amount</label>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-[13px] font-semibold text-[#9B8FA0] select-none">INR</span>
                    <input type="number" name="amount" value={form.amount} onChange={handleChange} placeholder="0.00" className={inputCls + " pl-14"} />
                  </div>
                  {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
                </div>

                {/* Payment Mode */}
                <div>
                  <label className={labelCls}>Payment Mode</label>
                  <div className="relative">
                    <Select
                      name="payment_mode"
                      value={form.payment_mode}
                      onChange={handleChange}
                      placeholder="Select payment mode"
                      className={selectCls}
                      options={paymentModes.map(m => ({ label: m?.toUpperCase(), value: m }))}
                    />
                  </div>
                  {errors.payment_mode && <p className="text-red-500 text-xs mt-1">{errors.payment_mode}</p>}
                </div>

                {/* Transaction ID / UTR No. */}
                <div>
                  <label className={labelCls}>Transaction ID / UTR No.</label>
                  <input name="transaction_id" value={form.transaction_id} onChange={handleChange} placeholder="UTR000000000000" className={inputCls + " placeholder:uppercase placeholder:tracking-wider"} />
                  {errors.transaction_id && <p className="text-red-500 text-xs mt-1">{errors.transaction_id}</p>}
                </div>

                {/* Payment Date */}
                <div>
                  <label className={labelCls}>Payment Date</label>
                  <DatePicker 
                    value={form.payment_date} 
                    onChange={handleChange} 
                    className={inputCls + " name=payment_date"} 
                  />
                  {errors.payment_date && <p className="text-red-500 text-xs mt-1">{errors.payment_date}</p>}
                </div>

                {/* Remarks */}
                <div>
                  <label className={labelCls}>Remarks <span className="normal-case font-normal text-[#C4B8CC]">(optional)</span></label>
                  <input
                    name="remark"
                    value={form.remark}
                    onChange={handleChange}
                    placeholder="Add a note for this request..."
                    className={inputCls}
                  />
                </div>
            {/* Upload Receipt */}
              <div>
                <label className={labelCls}>Upload Receipt</label>
                <label className={`flex items-center justify-between rounded-[12px] border px-5 py-2 cursor-pointer transition-all group ${
                  errors.receipt ? 'border-red-400 bg-red-50' : 'bg-[#F3F4F6] border-[#E8E2EE] hover:border-[#4B2364]/40 hover:bg-[#FAF5FF]'
                }`}>
                  <span className="text-[14px] text-[#C4B8CC] group-hover:text-[#9B8FA0] transition-colors">
                    {form.receipt ? form.receipt.name : "Upload"}
                  </span>
                  <div className="w-8 h-8 rounded-[8px] bg-white border border-[#E8E2EE] flex items-center justify-center group-hover:border-[#4B2364]/30 transition-colors shadow-sm">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9B8FA0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:stroke-[#4B2364] transition-colors">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </div>
                  <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf" onChange={handleFileChange} />
                </label>
                {errors.receipt && <p className="text-red-500 text-xs mt-1">{errors.receipt}</p>}
              </div>
              </div>

              {/* Submit Button */}
              <div className="mt-7">
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center gap-2 text-[18px] font-semibold text-white px-7 h-[48px] rounded-[12px] border-none cursor-pointer hover:opacity-90 transition-all shadow-[0px_6px_20px_rgba(220,38,38,0.25)] disabled:opacity-60 bg-secondary-gradient"
                >
                  {submitting ? "Submitting..." : "Submit Fund Request"}
                  {!submitting && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        <div className="bg-[#F3F4F5] rounded-[20px] shadow-[0px_8px_32px_rgba(75,35,100,0.07)] border border-[#CEC3D01A] mt-8">
          <div className="px-7 py-5 border-b border-[#F0EBF4] overflow-visible">
            <div className="flex items-center justify-between flex-wrap gap-4">

              <div className="flex items-center gap-3 flex-wrap">
                {/* Search */}
                <div className="relative flex items-center">
                  <svg className="absolute left-3" width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" strokeLinecap="round" /></svg>
                  <input type="text" placeholder="Search..." value={exportSearch} onChange={(e) => setExportSearch(e.target.value)}
                    className="bg-[#FFFFFF] rounded-[10px] pl-8 pr-4 py-2 text-[13px] text-[#191C1D] border-none outline-none w-44" />
                </div>
                {/* From Date */}
                <div className="flex items-center gap-2 bg-[#FFFFFF] rounded-[10px] px-3 py-2">
                  <span className="text-[11px] font-semibold text-[#4C444E] uppercase">From</span>
                  <input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)}
                    className="bg-transparent text-[13px] text-[#191C1D] outline-none cursor-pointer" />
                </div>
                {/* To Date */}
                <div className="flex items-center gap-2 bg-[#FFFFFF] rounded-[10px] px-3 py-2">
                  <span className="text-[11px] font-semibold text-[#4C444E] uppercase">To</span>
                  <input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)}
                    className="bg-transparent text-[13px] text-[#191C1D] outline-none cursor-pointer" />
                </div>
                {/* Status Dropdown */}
                <select value={exportStatus} onChange={(e) => setExportStatus(e.target.value)}
                  className="bg-[#FFFFFF] rounded-[10px] px-3 py-2 text-[13px] text-[#191C1D] border-none outline-none cursor-pointer">
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              {/* Export Split Button */}
                <div className="relative" ref={menuRef}>
                  <div className={`flex items-center rounded-lg overflow-hidden ${exporting ? 'opacity-60 pointer-events-none' : ''}`}>
                    <button
                      onClick={() => handleExport('csv')}
                      disabled={exporting}
                      className="flex items-center gap-2 bg-[#4B2364] px-4 py-[9px] text-[13px] font-semibold text-white cursor-pointer border-none transition-colors"
                    >
                      {exporting ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      )}
                      {exporting ? 'Exporting...' : 'Export'}
                    </button>
                    <button
                      onClick={() => setShowFormatMenu((v) => !v)}
                      disabled={exporting}
                      className="flex items-center justify-center bg-[#4B2364] px-2.5 h-[38px] text-white border-none cursor-pointer transition-colors"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`transition-transform duration-200 ${showFormatMenu ? 'rotate-180' : ''}`}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </div>

                  {showFormatMenu && (
                    <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden w-[160px]">
                      <div className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                        Choose Format
                      </div>
                      <button
                        onClick={() => { handleExport('csv'); setShowFormatMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors"
                      >
                        <span className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14,2 14,8 20,8" />
                            <line x1="8" y1="13" x2="16" y2="13" />
                            <line x1="8" y1="17" x2="16" y2="17" />
                          </svg>
                        </span>
                        CSV
                      </button>
                      <button
                        onClick={() => { handleExport('excel'); setShowFormatMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors"
                      >
                        <span className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14,2 14,8 20,8" />
                            <rect x="8" y="12" width="8" height="6" rx="1" />
                          </svg>
                        </span>
                        Excel
                      </button>
                    </div>
                  )}
                </div>
            </div>
          </div>
        </div>
          {/* Requests Table */}
          <div className="bg-white rounded-[20px] shadow-[0px_8px_32px_rgba(75,35,100,0.07)] border border-[#F0EBF4] overflow-hidden mt-8">
           

            <div className="overflow-x-auto">
                <div className="px-7 py-5 border-b border-[#F0EBF4]">
                  <p className="text-[20px] font-bold text-primary">Fund Request History</p>
                  {/* <p className="text-[13px] text-[#9B8FA0] mt-0.5">All your submitted fund requests</p> */}
                </div>
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F8F5FB]">
                    {["#", "Amount", "Payment Mode", "Transaction ID", "Virtual Account", "Status", "Date", "Action"].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-[12px] font-bold text-[#9B8FA0] uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <tr><td colSpan={8} className="text-center py-10 text-[#9B8FA0] text-sm">Loading...</td></tr>
                  ) : requests.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-10 text-[#9B8FA0] text-sm">No fund requests found.</td></tr>
                  ) : requests.map((r, i) => (
                    <tr key={r.fund_request_id} className="border-t border-[#F0EBF4] hover:bg-[#FAF8FC] transition-colors">
                      <td className="px-5 py-4 text-[13px] text-[#9B8FA0]">{(pagination.current_page - 1) * 10 + i + 1}</td>
                      <td className="px-5 py-4 text-[14px] font-bold text-primary">₹ {parseFloat(r.amount).toLocaleString()}</td>
                      <td className="px-5 py-4 text-[13px] text-[#4C444E] uppercase font-semibold">{r.payment_mode?.toUpperCase()}</td>
                      <td className="px-5 py-4 text-[13px] font-mono text-[#4C444E]">{r.transaction_id}</td>
                      <td className="px-5 py-4">
                        <div className="text-[13px] font-semibold text-primary">{r.virtual_account?.bank_name}</div>
                        <div className="text-[11px] text-[#9B8FA0]">{r.virtual_account?.account_number}</div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={statusBadge(r.status)}>{r.status}</span>
                      </td>
                      <td className="px-5 py-4 text-[12px] text-[#9B8FA0] whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => fetchView(r.fund_request_id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-primary-mid bg-primary-mid/5 hover:bg-primary-mid/10 transition-colors border-none cursor-pointer"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="flex items-center justify-between px-7 py-4 border-t border-[#F0EBF4]">
                <span className="text-[13px] text-[#9B8FA0]">Total {pagination.total} requests</span>
                <div className="flex gap-2">
                  {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => fetchRequests(p, {
                        search: exportSearch.trim(),
                        from_date: exportFrom,
                        to_date: exportTo,
                        status: exportStatus,
                      })}
                      className={`w-8 h-8 rounded-lg text-[13px] font-semibold border-none cursor-pointer transition-colors ${
                        p === pagination.current_page
                          ? "bg-primary-mid text-white"
                          : "bg-[#F3F4F5] text-[#4C444E] hover:bg-[#E8E2EE]"
                      }`}
                    >{p}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>

      {/* View Modal */}
      {viewData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewData(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="px-6 py-5 flex items-center justify-between bg-primary-gradient">
              <div>
                <p className="text-white font-bold text-[18px]">Fund Request Details</p>
                <p className="text-[#FFFFFFB2] text-[12px] mt-0.5 font-mono">{viewData.fund_request_id}</p>
              </div>
              <button onClick={() => setViewData(null)} className="text-white bg-white/20 hover:bg-white/30 rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold border-none cursor-pointer">×</button>
            </div>

            {viewLoading ? (
              <div className="py-16 text-center text-[#9B8FA0]">Loading...</div>
            ) : (
              <div className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">

                {/* Amount & Status */}
                <div className="flex items-center justify-between bg-[#F8F5FB] rounded-[12px] px-4 py-3">
                  <div>
                    <p className="text-[12px] text-[#9B8FA0] font-semibold">Amount</p>
                    <p className="text-[24px] font-bold text-primary">₹ {parseFloat(viewData.amount).toLocaleString()}</p>
                  </div>
                  <span className={statusBadge(viewData.status)}>{viewData.status}</span>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Payment Mode", value: viewData.payment_mode?.toUpperCase() },
                    { label: "Transaction ID", value: viewData.transaction_id },
                    { label: "Remark", value: viewData.remark || "—" },
                    { label: "Submitted On", value: new Date(viewData.created_at).toLocaleString("en-IN") },
                    ...(viewData.status === "approved" ? [{ label: "Approved At", value: viewData.approved_at ? new Date(viewData.approved_at).toLocaleString("en-IN"): "—",},] : []),
                      ...(viewData.status === "rejected" ? [ { label: "Rejection Reason", value: viewData.rejection_reason || "—",},] : []),
                      ].map(({ label, value }) => (
                        <div key={label} className="bg-[#F8F5FB] rounded-[10px] px-4 py-3">
                          <p className="text-[11px] text-[#9B8FA0] font-semibold mb-0.5">{label}</p>
                          <p className="text-[13px] font-semibold text-primary break-all">
                            {value || "—"}
                          </p>
                        </div>
                      ))}
                 </div>

                {/* Virtual Account */}
                <div className="bg-[#F0EBF4] rounded-[12px] px-4 py-3">
                  <p className="text-[12px] font-bold text-primary-mid mb-2">Virtual Account (Deposited To)</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: "Bank", value: viewData.virtual_account?.bank_name },
                      { label: "Account Holder", value: viewData.virtual_account?.account_holder_name },
                      { label: "Account No.", value: viewData.virtual_account?.account_number },
                      { label: "IFSC", value: viewData.virtual_account?.ifsc_code },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <p className="text-[11px] text-[#9B8FA0]">{label}</p>
                        <p className="text-[13px] font-semibold text-primary">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Source Bank */}
                <div className="bg-[#FFF8F0] rounded-[12px] px-4 py-3">
                  <p className="text-[12px] font-bold text-[#E98434] mb-2">Source Bank (Deposited From)</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: "Bank", value: viewData.source_bank?.bank_name },
                      { label: "Account Holder", value: viewData.source_bank?.account_holder_name },
                      { label: "Account No.", value: viewData.source_bank?.account_number },
                      { label: "IFSC", value: viewData.source_bank?.ifsc_code },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <p className="text-[11px] text-[#9B8FA0]">{label}</p>
                        <p className="text-[13px] font-semibold text-primary">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Receipt */}
                {viewData.receipt && (
                  <a href={viewData.receipt} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-4 py-3 bg-[#F3F4F5] rounded-[12px] text-[13px] font-semibold text-primary-mid hover:bg-[#E8E2EE] transition-colors no-underline"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    View Receipt
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}



